<script setup>
import { reactive, ref, onMounted, watch, onBeforeUnmount } from "vue";
import { useLoaderStore } from "@store/loader";
import useForm from "@composables/Form";
import { useRouter } from "vue-router";

const router = useRouter();
const props = defineProps({
    modelValue: {
        type: Boolean,
        default: false,
    },
    documentId: {
        type: [String, Number],
        default: null,
    },
});
const emit = defineEmits(["update:modelValue", "close", "openProject"]);

const handleClose = () => {
    emit("update:modelValue", false);
    window.location.reload();
};

const handleOpenProject = () => {
    // emit("update:modelValue", false);
    window.location.href = `/documents/${props.documentId}`;
};

function close() {
    document.getElementsByName("el-overlay-dialog").forEach((el) => {
        el.classList.remove("el-overlay-dialog-c");
    });

    emit("update:modelValue", false);
}

// While this modal is open, temporarily suspend wide-modal CSS on parent dialogs
watch(
    () => props.modelValue,
    (isOpen) => {
        const cls = "suspend-wide-dialog";
        if (isOpen) {
            document.body.classList.add(cls);
            // Add the class when modal opens
            setTimeout(() => {
                // Look for the modal with our specific classes
                const modal = document.querySelector('.el-dialog.standalone-dialog.client-dialog');
                if (modal) {
                    const overlayDialog = modal.closest('.el-overlay-dialog');
                    if (overlayDialog) {
                        overlayDialog.classList.add("el-overlay-dialog-c");
                    }
                } else {
                    // Fallback: look for modal containing success text
                    const allModals = document.querySelectorAll('.el-dialog');
                    for (const modal of allModals) {
                        const successText = modal.querySelector('.fw-bold');
                        if (successText && successText.textContent.includes('Project created successfully')) {
                            const overlayDialog = modal.closest('.el-overlay-dialog');
                            if (overlayDialog) {
                                overlayDialog.classList.add("el-overlay-dialog-c");
                                break;
                            }
                        }
                    }
                }
            }, 50);
        } else {
            document.body.classList.remove(cls);
        }
    },
    { immediate: true }
);

onBeforeUnmount(() => {
    document.body.classList.remove("suspend-wide-dialog");
});

onMounted(() => {
    // Add the class to the current modal's overlay dialog
    const addClassToOverlay = () => {
        // Look for the modal with our specific classes
        const modal = document.querySelector('.el-dialog.standalone-dialog.client-dialog');
        if (modal) {
            const overlayDialog = modal.closest('.el-overlay-dialog');
            if (overlayDialog) {
                overlayDialog.classList.add("el-overlay-dialog-c");
            }
        } else {
            // Fallback: look for modal containing success text
            const allModals = document.querySelectorAll('.el-dialog');
            for (const modal of allModals) {
                const successText = modal.querySelector('.fw-bold');
                if (successText && successText.textContent.includes('Project created successfully')) {
                    const overlayDialog = modal.closest('.el-overlay-dialog');
                    if (overlayDialog) {
                        overlayDialog.classList.add("el-overlay-dialog-c");
                        break;
                    }
                }
            }
        }
    };
    
    // Try immediately
    addClassToOverlay();
    
    // Also try after a short delay to ensure DOM is fully rendered
    setTimeout(addClassToOverlay, 100);
});
</script>
<template>
    <el-dialog
        :model-value="modelValue"
        style="width: 489px; !important"
        class="request-doc-dialog client-dialog standalone-dialog"
        align-center
        @close="handleClose"

        destroy-on-close
    >
        <div class="row g-3 text-center">
            <!-- Success Message -->

            <svg
                width="96"
                height="96"
                viewBox="0 0 96 96"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
            >
                <path
                    fill-rule="evenodd"
                    clip-rule="evenodd"
                    d="M48 0C60.8096 0.227335 73.0163 5.3844 81.9933 14.3614C90.9703 23.3384 96 35.4178 96 48C96 60.5822 90.9703 72.6616 81.9933 81.6386C73.0163 90.6156 60.8096 95.7727 48 96C35.1904 95.7727 22.9837 90.6156 14.0067 81.6386C5.02969 72.6616 0 60.5822 0 48C0 35.4178 5.02969 23.3384 14.0067 14.3614C22.9837 5.3844 35.1904 0.227335 48 0ZM37.8499 63.5925L25.8805 51.8269C24.9007 50.8614 24.3506 49.5538 24.3506 48.1907C24.3506 46.8276 24.9007 45.52 25.8805 44.5544C26.8652 43.5932 28.1972 43.0536 29.5857 43.0536C30.9741 43.0536 32.3061 43.5932 33.2909 44.5544L41.7227 52.8457L62.7091 32.2351C63.6923 31.2729 65.0237 30.7326 66.4117 30.7326C67.7997 30.7326 69.1311 31.2729 70.1142 32.2351C71.0927 33.2013 71.6419 34.5086 71.6419 35.8713C71.6419 37.2341 71.0927 38.5414 70.1142 39.5076L45.4199 63.7597C44.9186 64.2495 44.3218 64.6348 43.6651 64.8925C43.0085 65.1502 42.3056 65.275 41.5987 65.2594C40.8917 65.2438 40.1953 65.0881 39.5511 64.8016C38.9069 64.5152 38.3283 64.1039 37.8499 63.5925Z"
                    fill="#00BA00"
                />
            </svg>

            <h2 class="fw-bold mb-3" style="color: #079455; font-size: 24px">
                Document created successfully
            </h2>

            <!-- Sub Message -->
            <h6
                class=""
                style="color: #6b7280; font-size: 16px; font-weight: normal"
            >
                Our new document is now ready to use.
            </h6>

            <div class="d-flex justify-content-center" style="margin-bottom: 20px;">
                <button
                    type="button"
                    class="btn btn-light border px-4 py-3"
                    style="
                        width: 204.5px;
                        color: #374151;
                        border-color: #d1d5db;
                    "
                    @click="handleClose"
                >
                    Close
                </button>

                <!-- Open Project Button -->
                <button
                    type="button"
                    class="btn px-4 py-3"
                    style="
                        width: 204.5px;
                        background-color: #412CC9;
                        color: white;
                        border: none;
                    "
                    @click="handleOpenProject"
                >
                    Open Document
                </button>
            </div>
        </div>
    </el-dialog>
</template>
<style scoped>
.client-dialog :deep(.el-dialog) {
    border-radius: 16px;
    margin: 0 auto !important;
}

.client-dialog :deep(.el-dialog__header) {
    font-weight: 700;
}

.dialog-footer .btn {
    min-width: 120px;
    height: 44px;
    border-radius: 10px;
}

/* Center modal and constrain height with internal scrolling */

.client-dialog :deep(.el-dialog__wrapper) {
    display: flex;
    align-items: center;
    justify-content: center;
}

.client-dialog :deep(.el-dialog__body) {
    max-height: 70vh;
    overflow-y: auto;
}

/* Ensure this dialog ignores wide-modal global sizing */
.standalone-dialog :deep(.el-dialog) {
    width: 720px !important;
    max-width: 96vw !important;
    height: auto !important;
    margin: 0 auto !important;
}

.standalone-dialog :deep(.el-dialog__body) {
    height: auto !important;
    max-height: 70vh !important;
}

:deep(.el-overlay-dialog) {
    bottom: unset !important;
    padding: 10% 20%;
}
</style>
